// frontend/modules/template/hooks/useContextScripts.ts
import { useCallback, useMemo, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
    useGetAllScripts, 
    useCreateScript,
    useUpdateScript,
    useDeleteScript,
    useImportScripts,
    useExportScripts,
    useGetScriptByName,
    exportScripts as exportScriptsApi
} from '@/shared/api/client/context-script-controller';
import { useContextScriptStore } from '../stores/context-script-store';
import {
    ContextScriptImportData,
    ContextScriptExportData, ContextScript
} from '../types/context-script.types';
import { message } from 'antd';
import {ContextScriptDTO} from "@/shared/api/model";

export const useContextScripts = () => {
    const queryClient = useQueryClient();
    const { 
        scripts, 
        filters, 
        setScripts, 
        setFilters,
        clearFilters 
    } = useContextScriptStore();
    
    // API hooks - using Orval generated hooks directly
    const { data: scriptsResponse, isLoading } = useGetAllScripts({
        active: filters.active
    });
    
    const { mutateAsync: createScriptApi } = useCreateScript();
    const { mutateAsync: updateScriptApi } = useUpdateScript();
    const { mutateAsync: deleteScriptApi } = useDeleteScript();
    const { mutateAsync: importScriptsApi } = useImportScripts();
    
    // Effect to update scripts when data changes
    useEffect(() => {
        if (scriptsResponse) {
            setScripts(scriptsResponse);
        }
    }, [scriptsResponse, setScripts]);
    
    // Create script mutation
    const { mutateAsync: createScript } = useMutation({
        mutationFn: (script: ContextScriptDTO) => createScriptApi({ data: script }),
        onSuccess: () => {
            message.success('Script created successfully');
            queryClient.invalidateQueries({ queryKey: ['context-scripts'] });
        },
        onError: (err) => {
            console.error('Error creating script:', err);
            message.error('Failed to create script');
        }
    });
    
    // Update script mutation
    const { mutateAsync: updateScript } = useMutation({
        mutationFn: (script: ContextScriptDTO) => updateScriptApi({
            id: script.id||'',
            data: script 
        }),
        onSuccess: () => {
            message.success('Script updated successfully');
            queryClient.invalidateQueries({ queryKey: ['context-scripts'] });
        },
        onError: (err) => {
            console.error('Error updating script:', err);
            message.error('Failed to update script');
        }
    });
    
    // Delete script mutation
    const { mutateAsync: deleteScript } = useMutation({
        mutationFn: (id: string) => deleteScriptApi({ id }),
        onSuccess: () => {
            message.success('Script deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['context-scripts'] });
        },
        onError: (err) => {
            console.error('Error deleting script:', err);
            message.error('Failed to delete script');
        }
    });
    
    // Import scripts mutation
    const { mutateAsync: importScripts } = useMutation({
        mutationFn: (data: ContextScriptImportData) => importScriptsApi({ data }),
        onSuccess: (response) => {
            const result = response;
            if (result) {
                message.success(`Imported ${result.importedCount || 0} scripts successfully`);
                // Add null checks for optional properties
                if ((result.skippedCount && result.skippedCount > 0) || 
                    (result.errorCount && result.errorCount > 0)) {
                    message.warning(`Skipped ${result.skippedCount || 0} scripts, ${result.errorCount || 0} warnings`);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['context-scripts'] });
        },
        onError: (err) => {
            console.error('Error importing scripts:', err);
            message.error('Failed to import scripts');
        }
    });
    
    // Export scripts - using the direct API function instead of the hook response
    const exportScripts = useCallback(async () => {
        try {
            const response = await exportScriptsApi({});
            return response as ContextScriptExportData;
        } catch (err) {
            console.error('Error exporting scripts:', err);
            message.error('Failed to export scripts');
            throw err;
        }
    }, []);
    
    const refetchScripts = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['context-scripts'] });
    }, [queryClient]);
    
    // Computed values
    const filteredScripts = useMemo(() => {
        return scripts.filter(script => {
            if (filters.category && script.category !== filters.category) return false;
            if (filters.active !== undefined && script.active !== filters.active) return false;
            if (filters.search) {
                const search = filters.search.toLowerCase();
                return (
                    script?.name?.toLowerCase().includes(search) ||
                    (script.description && script.description.toLowerCase().includes(search))
                );
            }
            return true;
        });
    }, [scripts, filters]);
    
    return {
        scripts,
        filteredScripts,
        isLoading,
        filters,
        setFilters,
        clearFilters,
        createScript,
        updateScript,
        deleteScript,
        importScripts,
        exportScripts,
        refetchScripts
    };
};
